import { FC, ReactElement } from 'react';

import { EvSection, FormatHelper } from '@evinced-private/ui-common';

import {
	MOBILE_SUMMARY_PANE_ITEM_DAID,
	MOBILE_SUMMARY_PANE_ITEM_LABEL_DAID,
	MOBILE_SUMMARY_PANE_ITEM_VALUE_DAID
} from 'data-automation-ids/data-automation-ids';
import { generateDataAutomationIdAttribute } from 'src/common/helpers/DataAutomationIdHelper';

import { MobileSummaryPaneItem, SummaryValueItem } from '../../interfaces/MobileSummaryPaneItem';

import './MobileSummaryPane.scss';

interface IMobileSummaryPaneProps {
	items: MobileSummaryPaneItem[];
	className?: string;
	sectionLabel: string;
	dataAutomationId?: string;
	shouldHaveSkipLink?: boolean;
}

export const MobileSummaryPane: FC<IMobileSummaryPaneProps> = ({
	items,
	className = '',
	sectionLabel,
	dataAutomationId,
	shouldHaveSkipLink = true
}: IMobileSummaryPaneProps) => {
	const formatValue = (value: ReactElement | string | number): ReactElement | string => {
		return typeof value === 'number' ? FormatHelper.formatNumber(value) : value;
	};

	const renderValueItem = (
		{ primary, secondary }: SummaryValueItem,
		index: number
	): ReactElement => {
		return (
			<div
				key={index}
				className="value"
				aria-hidden={true}
				{...generateDataAutomationIdAttribute(MOBILE_SUMMARY_PANE_ITEM_VALUE_DAID)}
			>
				<div className="primary-value">{formatValue(primary)}</div>
				{secondary && <div className="secondary-value">{formatValue(secondary)}</div>}
			</div>
		);
	};

	const renderSummaryItem = ({ label, value, ariaLabel }: MobileSummaryPaneItem): ReactElement => {
		return (
			<li
				className="ev-mobile-summary-pane-item"
				key={label}
				{...generateDataAutomationIdAttribute(MOBILE_SUMMARY_PANE_ITEM_DAID)}
			>
				<div className="sr-only">{ariaLabel}</div>
				<div className="item-values" aria-hidden={true}>
					{value.map(renderValueItem)}
				</div>
				<div
					className="item-label"
					aria-hidden={true}
					{...generateDataAutomationIdAttribute(MOBILE_SUMMARY_PANE_ITEM_LABEL_DAID)}
				>
					{label}
				</div>
			</li>
		);
	};

	return (
		<EvSection
			skipLinkId={shouldHaveSkipLink ? 'summary' : null}
			className="ev-mobile-summary-pane-section"
			{...(dataAutomationId ? { dataAutomationId } : {})}
		>
			<ul className={`ev-mobile-summary-pane ${className}`} aria-label={sectionLabel}>
				{items.map(renderSummaryItem)}
			</ul>
		</EvSection>
	);
};
